import { ArrowLeft, CheckCircle2, Pencil, RotateCcw } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader } from "@/components/common/loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { completeCase, reopenCase } from "@/features/cases/api";
import { CaseFormDialog } from "@/features/cases/components/case-form-dialog";
import { CaseNotesPanel } from "@/features/cases/components/case-notes-panel";
import { CaseStatusBadge } from "@/features/cases/components/case-status-badge";
import { CaseStepsPanel } from "@/features/cases/components/case-steps-panel";
import { useCase } from "@/features/cases/hooks/use-case";
import { PipelineTracker } from "@/features/pipeline/components/pipeline-tracker";
import { isCaseDone } from "@/features/cases/types";
import { formatCents } from "@/lib/utils";

/** "Due in N days" / "OVERDUE by N days" / "Due today" from the signed delta. */
function dueLabel(days: number | null): string | null {
  if (days === null) return null;
  if (days === 0) return "Due today";
  if (days < 0) return `OVERDUE by ${-days} day${days === -1 ? "" : "s"}`;
  return `Due in ${days} day${days === 1 ? "" : "s"}`;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs font-medium">{label}</dt>
      <dd className="text-foreground text-sm font-semibold">{value}</dd>
    </div>
  );
}

/** A case tab that points at the client-level area for this resource. The Forms
 * and Documents libraries are client-scoped (a case attaches forms/docs via the
 * workflow), so the case tab links through to them. */
function ResourceTab({ feature, to }: { feature: string; to: string }) {
  return (
    <div className="text-muted-foreground grid place-items-center gap-3 py-12 text-center text-sm">
      <p className="font-medium">{feature}</p>
      <Button asChild variant="outline">
        <Link to={to}>Open {feature}</Link>
      </Button>
    </div>
  );
}

export function CaseDetail({ clientId, caseId }: { clientId: string; caseId: string }) {
  const { caseRecord, loading, error, notFound, setCase, refetch } = useCase(caseId);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState<"complete" | "reopen" | "resolvePrompt" | null>(
    null,
  );

  if (loading) return <Loader className="mt-10" />;
  if (notFound || !caseRecord) {
    return (
      <div className="mx-auto mt-16 flex max-w-md flex-col items-center gap-4 text-center">
        <h2 className="text-foreground text-xl font-bold">
          {notFound ? "Case not found" : "Couldn't load this case"}
        </h2>
        <p className="text-muted-foreground text-sm">
          {notFound
            ? "This case may have been removed, or you don't have access to it."
            : (error ?? "Something went wrong.")}
        </p>
        <Button asChild variant="outline">
          <Link to={`/client/${clientId}/cases`}>Back to cases</Link>
        </Button>
      </div>
    );
  }

  const c = caseRecord;
  const isDone = isCaseDone(c.status);
  const due = dueLabel(c.daysUntilDue);

  async function runComplete() {
    try {
      const updated = await completeCase(caseId);
      setCase(updated);
      toast.success("Case marked resolved");
    } catch {
      toast.error("Couldn't complete the case.");
    } finally {
      setConfirm(null);
    }
  }

  async function runReopen() {
    try {
      const updated = await reopenCase(caseId);
      setCase(updated);
      toast.success("Case reopened");
    } catch {
      toast.error("Couldn't reopen the case.");
    } finally {
      setConfirm(null);
    }
  }

  return (
    <div className="grid gap-5">
      <Link
        to={`/client/${clientId}/cases`}
        className="text-muted-foreground hover:text-primary flex w-fit items-center gap-1 text-sm"
      >
        <ArrowLeft className="size-4" /> Back to cases
      </Link>

      {/* Header */}
      <Card className="gap-4 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <h2 className="text-foreground text-2xl font-bold">
              {c.resolutionTypeDisplay}
            </h2>
            <CaseStatusBadge status={c.status} label={c.statusDisplay} />
            {due && !isDone && (
              <span
                className={
                  c.daysUntilDue !== null && c.daysUntilDue < 0
                    ? "text-destructive text-xs font-semibold"
                    : "text-muted-foreground text-xs font-semibold"
                }
              >
                {due}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="size-4" /> Edit
            </Button>
            {isDone ? (
              <Button variant="outline" size="sm" onClick={() => setConfirm("reopen")}>
                <RotateCcw className="size-4" /> Reopen
              </Button>
            ) : (
              <Button size="sm" onClick={() => setConfirm("complete")}>
                <CheckCircle2 className="size-4" /> Complete
              </Button>
            )}
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <Field label="Balance" value={formatCents(c.balanceCents)} />
          <Field label="Tax Years" value={c.taxYears || "—"} />
          <Field label="Assigned" value={c.assignedPractitionerName || "Unassigned"} />
          <Field label="Opened" value={c.openedOn} />
          <Field label="Target Date" value={c.targetDate || "—"} />
          <Field label="Follow-up" value={c.followupDate || "—"} />
          {c.followupDesc && <Field label="Follow-up Note" value={c.followupDesc} />}
          {c.closedAt && (
            <Field label="Closed" value={new Date(c.closedAt).toLocaleDateString()} />
          )}
        </dl>
      </Card>

      {/* Tabs */}
      <Card className="p-6">
        <Tabs defaultValue="steps">
          <TabsList>
            <TabsTrigger value="steps">
              Steps
              {c.openStepCount > 0 && (
                <span className="text-muted-foreground ml-1.5 text-xs">
                  ({c.openStepCount})
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="worksheets">Worksheets</TabsTrigger>
            <TabsTrigger value="forms">Forms</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="steps" className="mt-4">
            <CaseStepsPanel
              caseRecord={c}
              onChanged={refetch}
              onAllStepsComplete={() => !isDone && setConfirm("resolvePrompt")}
            />
          </TabsContent>
          <TabsContent value="pipeline" className="mt-4">
            <PipelineTracker caseId={caseId} />
          </TabsContent>
          <TabsContent value="notes" className="mt-4">
            <CaseNotesPanel caseId={caseId} onActivity={refetch} />
          </TabsContent>
          <TabsContent value="worksheets" className="mt-4">
            <div className="grid place-items-center gap-3 py-12 text-center">
              <p className="text-muted-foreground text-sm">
                Capture the client's full financial picture and compute RMI, offer
                amount, and installment-agreement terms.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Button asChild variant="outline">
                  <Link to={`/client/${clientId}/cases/${caseId}/worksheets/personal`}>
                    Open financial worksheet
                  </Link>
                </Button>
                {c.resolutionType === "IA" && (
                  <Button asChild>
                    <Link to={`/client/${clientId}/cases/${caseId}/workflow/ia`}>
                      Start IA Workflow
                    </Link>
                  </Button>
                )}
                {c.resolutionType === "OIC" && (
                  <Button asChild>
                    <Link to={`/client/${clientId}/cases/${caseId}/oic`}>
                      Start OIC Workflow
                    </Link>
                  </Button>
                )}
                {c.resolutionType === "EXCISE" && (
                  <Button asChild>
                    <Link to={`/client/${clientId}/cases/${caseId}/workflow/excise`}>
                      Start Excise (4180) Workflow
                    </Link>
                  </Button>
                )}
                {c.resolutionType === "INNOCENT_SPOUSE" && (
                  <>
                    <Button asChild>
                      <Link to={`/client/${clientId}/workflows/innocent-spouse`}>
                        Open ISA Workflow
                      </Link>
                    </Button>
                    <Button asChild variant="outline">
                      <Link to={`/client/${clientId}/cases/${caseId}/workflow/spouse-relief`}>
                        Open Spouse-Relief (8857) Workflow
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </TabsContent>
          <TabsContent value="forms" className="mt-4">
            <ResourceTab feature="IRS Forms" to={`/client/${clientId}/irs-forms`} />
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <ResourceTab feature="Documents" to={`/client/${clientId}/documents`} />
          </TabsContent>
        </Tabs>
      </Card>

      <CaseFormDialog
        clientId={clientId}
        caseRecord={c}
        open={editing}
        onOpenChange={setEditing}
        onSaved={refetch}
      />

      {/* Complete / Reopen / all-steps-done prompt */}
      <AlertDialog
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "reopen" ? "Reopen case?" : "Mark case resolved?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "reopen"
                ? "This sets the case back to Active and clears its closed date."
                : confirm === "resolvePrompt"
                  ? "All workflow steps are complete. Mark this case as resolved?"
                  : "This sets the case to Resolved and stamps its closed date."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => (confirm === "reopen" ? runReopen() : runComplete())}
            >
              {confirm === "reopen" ? "Reopen" : "Mark Resolved"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
